import { Injectable } from '@angular/core';
import { ProcedureEnum } from '@core/procedure-helpers/models/procedure.enum';
import { DirtyCheckPlugin } from '@datorama/akita/src/lib/plugins/dirtyCheck/dirtyCheckPlugin';
import { ApplianceDetailsStore } from '@modules/appliance-details/state/appliance-details.store';
import { DentureDetailsStore } from '@modules/denture-details/state/denture-details.store';
import { OrderFormConfirmationService } from '@modules/order/services/order-form-confirmation.service';
import { ProcedureConfigurationService } from '@modules/order/services/procedure-configuration.service';
import { OrderQuery } from '@modules/order/state/order-query';
import { OrderStore } from '@modules/order/state/order-store';
import { TeethDiagramQuery } from '@modules/teeth-diagram/state/teeth-diagram-query';
import { TeethDiagramStore } from '@modules/teeth-diagram/state/teeth-diagram-store';
import { PopUpActions } from '@shared/models/enums/popup-modal-actions.enum';
import { of, tap, map, Observable } from 'rxjs';

@Injectable({
	providedIn: 'root'
})
export class ProcedureTypeService {
	private static readonly procedureIdsRequiringTeethReset: ProcedureEnum[] = [
		ProcedureEnum.ImplantPlanning,
		ProcedureEnum.Denture_Removable
	];

	private teethDiagramDirtyCheck: DirtyCheckPlugin;

	constructor(
		private readonly orderStore: OrderStore,
		private readonly orderQuery: OrderQuery,
		private readonly procedureConfigurationService: ProcedureConfigurationService,
		private readonly orderFormConfirmationService: OrderFormConfirmationService,
		private readonly teethDiagramQuery: TeethDiagramQuery,
		private readonly teethDiagramStore: TeethDiagramStore,
		private readonly dentureDetailsStore: DentureDetailsStore,
		private readonly applianceDetailsStore: ApplianceDetailsStore
	) {
		this.teethDiagramDirtyCheck = new DirtyCheckPlugin(this.teethDiagramQuery).setHead();
	}

	handleProcedureTypeChanged(procedureTypeId: number): Observable<boolean> {
		const currentProcedureMap = this.orderQuery.procedureMap;

		if (!currentProcedureMap) {
			return of(true);
		}

		const currentProcedureType = currentProcedureMap.TypeId;

		if (procedureTypeId === currentProcedureType) {
			return of(true);
		}

		const procedureId = currentProcedureMap.ProcedureId;

		this.resetStoresAfterProcedureTypeChange();

		const currentSendTo = this.orderQuery.sendTo;
		const procedureMap =
			this.procedureConfigurationService.getProcedureMap(procedureId, procedureTypeId, currentSendTo) ??
			this.procedureConfigurationService.getProcedureMap(procedureId, procedureTypeId);

		const availableLabs = this.procedureConfigurationService.getAvailableLabs({
			procedureId,
			typeId: procedureTypeId
		});
		const shouldResetSendTo = !availableLabs?.some(lab => lab?.Id === currentSendTo?.Id);
		const orderPatch = {
			procedureMap,
			availableLabs,
			...(shouldResetSendTo ? { sendTo: null } : {})
		};

		const canProceed$ =
			this.requiresTeethResetConfirmation(procedureMap?.ProcedureId) && this.teethDiagramDirtyCheck.isDirty()
				? this.orderFormConfirmationService
						.openPopUp$(true)
						.pipe(map(dialogResult => dialogResult === PopUpActions.Ok))
						.pipe(
							tap(allowed => {
								if (allowed) {
									this.teethDiagramStore.resetTeeth();
									this.teethDiagramDirtyCheck.setHead();
								}
							})
						)
				: of(true);

		return canProceed$.pipe(
			map(allowed => {
				if (allowed) {
					this.orderStore.update(orderPatch);

					return true;
				}

				// prevent update store if user does not allow to reset teeth
				return false;
			})
		);
	}

	private resetStoresAfterProcedureTypeChange(): void {
		this.dentureDetailsStore.update({ isUpperJawChecked: false, isLowerJawChecked: false });
		this.applianceDetailsStore.resetType();
	}

	private requiresTeethResetConfirmation(procedureId: number | undefined): boolean {
		return ProcedureTypeService.procedureIdsRequiringTeethReset.includes(procedureId as ProcedureEnum);
	}
}
